import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Info, SettingsOutlined, ChevronRight } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { StyleObj } from '../../@types';
import { Bet, BettingSlipWithMonitoringInfo } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { BETTING_SLIP_FROM } from '../../constants/enums';
import { useModal } from '../../contexts/ModalContext';
import {
  initializeTicketAuthorizationFormData,
  formatScore,
  getNameToDisplay,
  getTicketBettingType,
} from '../../helpers';
import { defaultColumnsTicketAuthorizationDetails } from '../../helpers/table';
import { betBuilderGroupRows, isBetBuilderRow } from '../../helpers/ticketMonitoring';
import BetBuilderBadge from '../atoms/BetBuilderBadge';
import useTicketAuthorization from '../../hooks/useTicketAuthorization';
import useVisibleColumns from '../../hooks/useVisibleColumns';
import { ticketReofferSchema } from '../../schema';
import { createColumn } from '../../utils';
import { getData } from '../../utils/api';
import IconRenderer from '../atoms/IconRenderer';
import { BettingSlipFromIcon } from '../icons';
import CustomizeTable from '../molecules/CustomizeTable';
import BetBuilderSelectionsList from '../molecules/BetBuilderSelectionsList';
import FormNumberInput from '../molecules/FormNumberInput';
import TicketAuthCountdown from '../molecules/TicketAuthCountdown';
import TicketAuthSpecialValueSelect from '../molecules/TicketAuthSpecialValueSelect';
import TicketAuthDetailsDataGrid from './TicketAuthDetailsDataGrid';
import { calculateTotalOddsForNormalBettingSlip, calculateMaxPayout } from 'sportsbook-shared-module';
import theme from '../../styles/theme';
import { useMarketplace } from '../../hooks/useMarketplace';

import { mergeSx } from '../../utils/styles';

const styles: StyleObj = {
  container: {
    mt: 11,
    width: '50%',
    borderRadius: 2,
    height: {
      xs: 'calc(100% - 100px)',
      md: 'calc(100% - 180px)',
    },
    justifyContent: 'space-between',
    gap: {
      xs: 0.25,
      md: 3,
    },
  },
  generalInfoRow: {
    backgroundColor: 'primary.main',
    color: 'white',
    display: 'flex',
  },
  generalInfoWrapper: {
    height: 44,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    pr: 1.75,
    pl: {
      xs: 0,
      md: 1.5,
    },
    gap: {
      xs: 1.5,
      md: 3,
    },
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'background.lighter',
    px: 1.75,
    py: 1.25,
  },
  customizeTableIcon: {
    color: 'white',
    mr: -1,
    ':hover': {
      color: 'background.lightGreen',
      background: 'transparent',
    },
  },
  numberInput: {
    '& .MuiOutlinedInput-input': {
      padding: '2px 6px',
      backgroundColor: 'white',
      width: 64,
      fontWeight: 600,
      fontSize: 14,
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        fontSize: 12,
      },
    },
  },

  actionsContainer: (theme) => ({
    position: 'absolute',
    bottom: 40,
    right: 40,
    '> button': {
      height: 32,
      width: 90,
    },
    mt: 1,
    [theme.breakpoints.down('md')]: {
      position: 'fixed',
    },
  }),
  chevronIconBtn: (theme) => ({
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  }),
  desktopTimerWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    position: 'absolute',
    top: 12,
    right: 24,
  },
  tableWrapper: {
    maxHeight: '75%',
    height: '-webkit-fill-available',
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  dataGridWrapper: {
    height: 'calc(100% - 89px)',
    overflow: 'auto',
  },
  authResultsWrapper: {
    height: { md: '25%', sm: '30%' },
    maxHeight: '200px',
    overflow: 'scroll',
  },
  authResultItem: {
    backgroundColor: 'background.lighter',
    borderRadius: 2,
    mb: 1,
    p: 1.25,
  },
};

const getPunterText = (ticketDetails?: BettingSlipWithMonitoringInfo) => {
  const punter = ticketDetails?.user?.username;
  const ticketsCount = ticketDetails?.totalAcceptedTicketCount;
  return `${punter ?? '--'} (${ticketsCount ?? '--'})`;
};

type Props = {
  ticketId: string;
  handleClose: () => void;
};

const TicketAuthDetailsTable = ({ ticketId, handleClose }: Props) => {
  const { data: ticketDetails, isPending } = useQuery({
    queryKey: [QUERY_KEYS.bettingSlips, ticketId],
    queryFn: (): Promise<BettingSlipWithMonitoringInfo> => getData(`/betting-slips/${ticketId}`),
    enabled: !!ticketId,
  });

  const { isOMFeed, isFTBFeed } = useMarketplace();

  const [winnings, setWinnings] = useState(0);
  const [totalOdds, setTotalOdds] = useState<number | null>(null);
  const [specialValuesChanged, setSpecialValuesChanged] = useState(false);

  const { openModal } = useModal();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const betHasSpecialValue = useCallback(
    (bet: Bet) => (isOMFeed && !!bet.specialValues.length) || (isFTBFeed && !!bet.outcome.properties?.specialValue),
    [isOMFeed, isFTBFeed]
  );

  const initialFormValues = useMemo(
    () => initializeTicketAuthorizationFormData(ticketDetails, isOMFeed),
    [ticketDetails, isOMFeed]
  );

  const {
    control,
    reset,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    defaultValues: initialFormValues,
    resolver: zodResolver(ticketReofferSchema(ticketDetails?.totalStakeAmount || 0)),
  });

  const { acceptTicket, rejectTicket, reofferTicket, isSubmitting } = useTicketAuthorization(
    ticketDetails,
    handleClose
  );

  useEffect(() => {
    if (ticketDetails) {
      reset(initialFormValues);
      setWinnings(ticketDetails.possibleWinnings);
      setTotalOdds(ticketDetails.totalOdds);
    }
  }, [initialFormValues, reset, ticketDetails]);

  const recalculateTicketInfo = useCallback(() => {
    if (!ticketDetails) return;

    const currentOdds = getValues('odds');
    const totalStakeAmount = getValues('totalStakeAmount');
    const specialValues = getValues('specialValues');

    const preparedBets = ticketDetails.bets.map((bet) => ({
      ...bet,
      eventId: bet.event.id,
      marketTypeId: bet.market.marketType.id,
      marketTypeCombiningIds: bet.market.marketType.marketTypeCombiningIds,
      odds:
        currentOdds?.[bet.outcome.id]?.toString() ||
        specialValues?.[bet.id]?.[0]?.odds?.toString() ||
        bet.odds.toString(),
    }));

    const recalculatedOdds =
      ticketDetails.bettingSlipType !== 'System' ? calculateTotalOddsForNormalBettingSlip(preparedBets) : null;
    const { maxPayout } = calculateMaxPayout(
      {
        bets: preparedBets,
        betTypes: [
          {
            requiredHitCount: ticketDetails.requiredHitCount,
            stakeAmountPerCombination: Number(totalStakeAmount) / ticketDetails.combinationsCount,
          },
        ],
      },
      Infinity,
      Infinity
    );

    setWinnings(maxPayout);
    setTotalOdds(recalculatedOdds);
  }, [ticketDetails, getValues, setWinnings, setTotalOdds]);

  const handleSpecialValueChange = useCallback(
    (selectedOutcomeId: string, selectedMarketId: string, betId: string, selectedOdds: string, name: string) => {
      setValue(`specialValues.${betId}.0`, {
        id: selectedOutcomeId,
        marketId: selectedMarketId,
        odds: selectedOdds,
        name,
      });
      recalculateTicketInfo();
      setSpecialValuesChanged(true);
    },
    [setValue, recalculateTicketInfo, setSpecialValuesChanged]
  );

  const handleBetChange = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        recalculateTicketInfo();
      }
    },
    [recalculateTicketInfo]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('startDate', 'Date and Time', {
        valueGetter: (value, row, column, apiRef) => {
          return row.event.startDate ? dayjs(row.event.startDate).format('DD-MM-YYYY HH:mm') : '';
        },
        sortable: false,
        alwaysVisible: true,
        flex: 1.5,
      }),
      createColumn('icon', 'Icon', {
        renderCell: (params: GridRenderCellParams<Bet>) => {
          return <IconRenderer name={params.row.icon} />;
        },
        flex: 0.8,
        sortable: false,
        alwaysVisible: true,
      }),
      createColumn('from', 'From', {
        renderCell: (params: GridRenderCellParams<Bet>) => {
          const from = params.row.isLive ? 'In-play' : 'Pre-match';
          return <BettingSlipFromIcon from={from} />;
        },
        sortable: false,
        flex: 0.8,
      }),
      createColumn('competitionName', 'Competition', {
        sortable: false,
        minWidth: 100,
        flex: 1.5,
      }),
      createColumn('tournamentName', 'Tournament', {
        sortable: false,
        minWidth: 150,
        flex: 1.5,
      }),
      createColumn('event', 'Event', {
        valueGetter: (value, row, column, apiRef) => {
          return row.event.name;
        },
        sortable: false,
        flex: 2.5,
        minWidth: 200,
        alwaysVisible: true,
      }),
      createColumn('banker', 'Banker', {
        renderCell: (params: GridRenderCellParams<Bet>) => {
          return params.row.banker ? <CheckCircle color='success' /> : '';
        },
        sortable: false,
        flex: 0.8,
      }),
      createColumn('isWays', 'Ways', {
        renderCell: (params: GridRenderCellParams<Bet>) => {
          return params.row.ways ? <CheckCircle color='success' /> : '';
        },
        sortable: false,
        flex: 0.8,
      }),
      createColumn('market', 'Market', {
        valueGetter: (value, row, column, apiRef) => {
          if (isOMFeed && row.market.marketType) {
            return getNameToDisplay(row.market.marketType);
          }

          return row.market.name;
        },
        sortable: false,
        alwaysVisible: true,
        flex: 1.5,
        minWidth: 150,
      }),
      createColumn('specialValue', 'SV', {
        renderCell: (params: GridRenderCellParams<Bet>) => {
          if (isBetBuilderRow(params.row)) return null;
          if (!betHasSpecialValue(params.row)) {
            return null;
          }

          return (
            <TicketAuthSpecialValueSelect
              control={control}
              bet={params.row}
              name={`specialValues.${params.row.id}.0.id`}
              onSpecialValueChange={handleSpecialValueChange}
            />
          );
        },
        sortable: false,
        alwaysVisible: true,
        flex: 1.5,
      }),
      createColumn('outcomeShortName', 'Outcome Short Name', {
        valueGetter: (value, row, column, apiRef) => row.outcome.shortName,
        sortable: false,
        minWidth: 150,
      }),
      createColumn('outcomeName', 'Outcome Name', {
        valueGetter: (value, row, column, apiRef) => row.outcome.name,
        sortable: false,
        minWidth: 150,
      }),
      createColumn('odds', 'Odds', {
        sortable: false,
        alwaysVisible: true,
        renderCell: (params: GridRenderCellParams<Bet>) => {
          if (isBetBuilderRow(params.row)) {
            return <Typography variant='body3'>{params.value}</Typography>;
          }

          return (
            <FormNumberInput
              control={control}
              allowDecimals
              valueAsString
              onKeyDown={handleBetChange}
              name={
                betHasSpecialValue(params.row)
                  ? `specialValues.${params.row.id}.0.odds`
                  : `odds.${params.row.outcome.id}`
              }
              showHelperText={false}
              sx={styles.numberInput}
            />
          );
        },
      }),
      createColumn('result', 'Result', {
        valueGetter: (value, row, column, apiRef) => formatScore(row.mainEventPartScore),
        sortable: false,
      }),
      createColumn('betBuilder', 'Bet Builder', {
        renderCell: (params: GridRenderCellParams<Bet>) => {
          return <BetBuilderBadge enabled={isBetBuilderRow(params.row)} />;
        },
        sortable: false,
        flex: 0.8,
      }),
    ],
    [betHasSpecialValue, control, handleBetChange, handleSpecialValueChange, isOMFeed]
  );

  const rows = useMemo(
    () => [...(ticketDetails?.bets || []), ...betBuilderGroupRows(ticketDetails?.betBuilder)],
    [ticketDetails]
  );

  const { visibleColumns, setVisibleColumns, visibleColumnsLSKey } = useVisibleColumns({
    manualLSKey: 'ticket-authorization-details-table', // Used to differentiate tables on same path
    columns,
    defaultVisibleColumns: defaultColumnsTicketAuthorizationDetails,
  });

  const openCustomizationDialog = () => {
    openModal(
      <CustomizeTable
        columns={columns}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultColumnsTicketAuthorizationDetails}
        visibleColumnsLSKey={visibleColumnsLSKey}
      />
    );
  };

  return (
    <Stack sx={styles.container}>
      <Box sx={styles.tableWrapper}>
        <Box sx={styles.desktopTimerWrapper}>
          {ticketDetails && !isMobile && (
            <TicketAuthCountdown ticketId={ticketId} createdAt={ticketDetails.createdAt} />
          )}
        </Box>
        <Box sx={styles.generalInfoRow}>
          <IconButton onClick={handleClose} sx={styles.chevronIconBtn} color='inherit'>
            <ChevronRight />
          </IconButton>
          <Stack sx={styles.generalInfoWrapper}>
            {ticketDetails && isMobile && (
              <TicketAuthCountdown ticketId={ticketId} createdAt={ticketDetails.createdAt} />
            )}
            <Typography variant='h5'>{ticketDetails?.ticketId}</Typography>
            <Typography variant='h5' sx={styles.capitalize}>
              {ticketDetails?.device}
            </Typography>
            <Typography variant='h5'>Punter: {getPunterText(ticketDetails)}</Typography>
            <Typography variant='h5'>Agent: {ticketDetails?.agent?.fullName}</Typography>
            <Typography variant='h5' sx={styles.capitalize}>
              {ticketDetails?.walletType}
            </Typography>
            <Typography variant='h5'>
              {ticketDetails?.from === BETTING_SLIP_FROM.PRE_MATCH
                ? ticketDetails?.user.preMatchRiskFactor
                : ticketDetails?.user.inPlayRiskFactor}
            </Typography>
            <Typography variant='h5'>{getTicketBettingType(ticketDetails)}</Typography>
            <IconButton onClick={openCustomizationDialog} sx={styles.customizeTableIcon}>
              <SettingsOutlined />
            </IconButton>
          </Stack>
        </Box>
        <Box sx={styles.infoRow}>
          <Stack
            direction='row'
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography
              variant='body3'
              sx={{
                fontWeight: 600,
              }}
            >
              Stake:
            </Typography>
            <FormNumberInput
              control={control}
              allowDecimals
              valueAsString
              name='totalStakeAmount'
              onKeyDown={handleBetChange}
              error={errors?.totalStakeAmount}
              showHelperText={false}
              sx={styles.numberInput}
            />
          </Stack>
          <Typography
            variant='body3'
            sx={{
              fontWeight: 600,
            }}
          >
            Odds: {!ticketDetails?.isWays && totalOdds?.toFixed(2)}
          </Typography>
          <Typography
            variant='body3'
            sx={{
              fontWeight: 600,
            }}
          >
            Possible Winnings: {winnings?.toFixed(2)}
          </Typography>
          <Typography
            variant='body3'
            sx={{
              fontWeight: 600,
            }}
          >
            Num. of Bets: {ticketDetails?.betsCount}
          </Typography>
          <Typography
            variant='body3'
            sx={{
              fontWeight: 600,
            }}
          >
            Combinations: {ticketDetails?.combinationsCount}
          </Typography>
        </Box>
        <Box sx={styles.dataGridWrapper}>
          <TicketAuthDetailsDataGrid
            columns={columns}
            bets={rows}
            visibleColumns={visibleColumns}
            loading={isPending}
            getDetailPanelContent={(params) => {
              if (!isBetBuilderRow(params.row)) return null;
              return <BetBuilderSelectionsList selections={params.row.selections} />;
            }}
          />
        </Box>
      </Box>
      <Box sx={styles.authResultsWrapper}>
        <Box>
          {ticketDetails?.authResults.map((condition) => (
            <Stack
              key={condition.conditionTypeId}
              direction='row'
              spacing={1}
              sx={mergeSx(
                {
                  alignItems: 'center',
                },
                styles.authResultItem
              )}
            >
              <Info color='primary' />
              <Typography variant='body3'>{condition.adminMessage}</Typography>
            </Stack>
          ))}
        </Box>
        <Stack
          direction='row'
          spacing={1}
          sx={mergeSx(
            {
              justifyContent: 'end',
            },
            styles.actionsContainer
          )}
        >
          <Button onClick={rejectTicket} color='error' disabled={isSubmitting}>
            Reject
          </Button>
          <Button
            onClick={handleSubmit((data) => reofferTicket(data, ticketDetails?.bets || []))}
            variant='outlined'
            disabled={isSubmitting || !isValid || (!isDirty && !specialValuesChanged)}
          >
            Reoffer
          </Button>
          <Button onClick={acceptTicket} disabled={isSubmitting}>
            Accept
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export default TicketAuthDetailsTable;
